import { provideHttpClient, withInterceptorsFromDi, withFetch } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, LOCALE_ID } from '@angular/core';
import { provideClientHydration } from '@angular/platform-browser';
import {
  isActive,
  IsActiveMatchOptions,
  provideRouter,
  Router,
  RouteReuseStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { CookieConfig, ProvideCookiesNgrx } from '@teamfoster/sdk/cookie-ngrx';
import { ProvideDictionaryNgrx } from '@teamfoster/sdk/dictionary-ngrx';
import { ProvideIcons } from '@teamfoster/sdk/icon';
import { ImageSharpConfig, ProvideOptimisedImageProcessor } from '@teamfoster/sdk/image';
import { ProvideMenuNgrx } from '@teamfoster/sdk/menu-ngrx';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { reducers, metaReducers, appEffects, CustomSerializer } from './store';

import { provideAnimations } from '@angular/platform-browser/animations';
import { ProvideLocationNgrx } from './location/location.providers';
import { ProvideSocialsNgrx } from './social-media/socials.providers';
import { AppRouteReuseStrategy } from './app.route-reuse-strategy';
import { HookParserEntry, provideDynamicHooks } from 'ngx-dynamic-hooks';
import { ContentSliderComponent } from './slider/content-slider/content-slider.component';
import { ProvideSliderNgrx } from './slider/slider.providers';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { ContentAccordeonComponent } from './standalone/components/content-accordeon/content-accordeon.component';

import localeNl from './nl';
import { registerLocaleData } from '@angular/common';
import { ContentPanelComponent } from './standalone/components/content-panel/content-panel.component';

import gsap from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const cookieConfig: CookieConfig = {
  analyticsCode: 'GTM-MZ2PGK3',
  useTagManager: true,
  strippedContentPlaceholder: 'Deze inhoud wordt niet getoond omdat je geen marketing cookies hebt geaccepteerd',
  buttonSettings: 'Instellingen',
  buttonAcceptAll: 'Accepteer alle cookies',
  buttonClose: 'Sluiten',
  strippedContentButton: 'Cookie-instellingen wijzigen',
  types: {
    analytical: true,
    functional: true,
    marketing: true,
  },
  showSettings: true,
};

registerLocaleData(localeNl, 'nl');

// This automatically creates SelectorHookParsers for each listed component:
const componentParsers: Array<HookParserEntry> = [
  { component: ContentSliderComponent },
  { component: ContentAccordeonComponent },
  { component: ContentPanelComponent },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideRouter(
      routes,
      withViewTransitions({
        onViewTransitionCreated: ({ transition }) => {
          const router = inject(Router);
          const targetUrl = router.currentNavigation()!.finalUrl!;
          // Skip transition if only fragment or query params change
          const config: IsActiveMatchOptions = {
            paths: 'exact',
            matrixParams: 'exact',
            fragment: 'ignored',
            queryParams: 'ignored',
          };
          const isTargetRouteCurrent = isActive(targetUrl, router, config);
          if (isTargetRouteCurrent()) {
            transition.skipTransition();
          }
        },
      }),
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'disabled', anchorScrolling: 'enabled' })
    ),
    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    provideEffects(appEffects),
    provideRouterStore({ serializer: CustomSerializer }),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
    ProvideDictionaryNgrx({}),
    ProvideMenuNgrx({}),
    ProvideLocationNgrx({}),
    ProvideSocialsNgrx({}),
    ProvideSliderNgrx({}),
    ProvideIcons({ spritesheetUrl: 'assets/dist/svg/symbol/svg/sprite.symbol.svg' }),
    ProvideCookiesNgrx(cookieConfig),
    ProvideOptimisedImageProcessor(ImageSharpConfig, {
      placeholderUrl: 'https://coda.fstr.io/media/coda-pattern-green.png',
      defaultFormat: 'webp',
    }),
    provideClientHydration(),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideDynamicHooks({
      parsers: componentParsers,
    }),
    { provide: LOCALE_ID, useValue: 'nl' },
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: '6Le2vQIrAAAAAB1S9FgFOkK7N3qLSfciKAhgfudI' },
  ],
};
